import { html } from "lit"
import { repeat } from "lit/directives/repeat.js"
import { unsafeHTML } from "lit/directives/unsafe-html.js"
import type { Message } from "../api/contract/query"
import type { Messages } from "../i18n/messages"
import { normalizeDisplayName } from "../utils/display-name"
import { sanitizeFormattedBody } from "../utils/formatted-body-sanitizer"
// Registers <cumments-avatar>, the shared avatar presentation used below.
import "./avatar"
import type { CommentViewModel } from "./view-model"

// Elements that are meaningful even without text content (structural/visible elements)
const MEANINGFUL_EMPTY_ELEMENTS = new Set(["br", "hr", "img"])

/**
 * Check if sanitized HTML contains meaningful visible content.
 * Returns false for empty strings, whitespace-only content, or documents
 * containing only empty structural elements (e.g. <p></p>, <blockquote> </blockquote>).
 * Uses a DOM-based check to detect actual user-visible content.
 * Elements like <br> are considered meaningful even without text.
 */
function hasMeaningfulContent(html: string): boolean {
  const trimmed = html.trim()
  if (trimmed.length === 0) return false

  const container = document.createElement("div")
  container.innerHTML = trimmed

  // Check for meaningful text content
  if (container.textContent?.trim().length) return true

  // Check for structural/visible elements that have no text (e.g. <br>, <hr>, <img>)
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT)
  let node: Node | null = walker.nextNode()
  while (node) {
    if (MEANINGFUL_EMPTY_ELEMENTS.has((node as Element).tagName.toLowerCase())) {
      return true
    }
    node = walker.nextNode()
  }

  return false
}

// Content rendering: Message is source of truth
export function renderContent(message: Message) {
  const c = message.content as unknown as Record<string, unknown>
  // redacted tombstone
  if (
    c.type === "redacted" ||
    (message as unknown as Record<string, unknown>).status === "redacted"
  ) {
    return html`<span style="color:#94a3b8;font-style:italic">— deleted —</span>`
  }
  if (c.type === "encrypted") {
    return html`<span style="color:#94a3b8;font-style:italic">— encrypted —</span>`
  }
  if (c.type === "unknown") {
    const fallback = (c.fallback as string | null) ?? (c.body as string | null) ?? ""
    return html`${fallback}`
  }
  if (c.type === "text") {
    const body = (c.body as string | undefined) ?? ""
    const formatted = c.formatted_body as string | null | undefined
    if (formatted) {
      const sanitized = sanitizeFormattedBody(formatted)
      if (hasMeaningfulContent(sanitized)) {
        return html`${unsafeHTML(sanitized)}`
      }
    }
    return html`${body}`
  }
  if (c.type === "media") {
    const kind = (c.kind as string) ?? "file"
    const url = (c.url as string) ?? ""
    const proxyUrl = (c.thumbnail_url as string) ?? url
    const filename = (c.filename as string) ?? ""
    const alt = (c.alt_text as string) ?? filename ?? ""
    const width = c.width as number | null | undefined
    const height = c.height as number | null | undefined
    const mimetype = (c.mimetype as string) ?? ""
    const isImage = kind === "image" || kind === "sticker" || mimetype.startsWith("image/")
    const isVideo = kind === "video" || mimetype.startsWith("video/")
    const isAudio = kind === "audio" || mimetype.startsWith("audio/")
    if (isImage) {
      return html`<div style="margin:6px 0">
        <img src="${proxyUrl}" alt="${alt}" loading="lazy" style="max-width:100%;max-height:320px;border-radius:8px;border:1px solid #e2e8f0" width="${width ?? ""}" height="${height ?? ""}" />
        ${filename ? html`<div style="font-size:12px;color:#64748b;margin-top:4px">${filename}</div>` : ""}
      </div>`
    }
    if (isVideo) {
      return html`<div style="margin:6px 0">
        <video src="${url}" controls style="max-width:100%;max-height:320px;border-radius:8px"></video>
        ${filename ? html`<div style="font-size:12px;color:#64748b">${filename}</div>` : ""}
      </div>`
    }
    if (isAudio) {
      return html`<div style="margin:6px 0">
        <audio src="${url}" controls style="width:100%"></audio>
        ${filename ? html`<div style="font-size:12px;color:#64748b">${filename}</div>` : ""}
      </div>`
    }
    // file / sticker fallback
    return html`<div style="margin:6px 0;display:flex;align-items:center;gap:8px;border:1px solid #e2e8f0;border-radius:8px;padding:8px">
      <span style="font-size:20px">${kind === "sticker" ? "⭐" : "📎"}</span>
      <a href="${url}" target="_blank" rel="noopener" style="color:#4f46e5;word-break:break-all">${filename || url}</a>
      ${mimetype ? html`<span style="font-size:11px;color:#94a3b8">${mimetype}</span>` : ""}
    </div>`
  }
  if (c.type === "location") {
    const geo = (c.geo_uri as string) ?? ""
    const desc = (c.description as string | null) ?? ""
    const thumb = c.thumbnail_url as string | null | undefined
    return html`<div style="margin:6px 0;border:1px solid #e2e8f0;border-radius:8px;padding:8px">
      ${thumb ? html`<img src="${thumb}" alt="" loading="lazy" style="max-width:100%;border-radius:8px;margin-bottom:6px" />` : ""}
      <div style="font-size:13px;color:#1e293b">${desc || geo}</div>
      ${geo ? html`<a href="https://www.openstreetmap.org/?mlat=${encodeURIComponent(geo.replace(/^geo:/, "").split(",")[0] ?? "")}&mlon=${encodeURIComponent(geo.replace(/^geo:/, "").split(",")[1]?.split(";")[0] ?? "")}#map=16/${encodeURIComponent(geo.replace(/^geo:/, "").split(",")[0] ?? "")}/${encodeURIComponent(geo.replace(/^geo:/, "").split(",")[1]?.split(";")[0] ?? "")}" target="_blank" rel="noopener" style="font-size:12px;color:#4f46e5">${geo}</a>` : ""}
    </div>`
  }
  if (c.type === "poll") {
    const question = (c.question as string) ?? ""
    const options = (c.options as Array<{ id: string; text: string }>) ?? []
    const responses = (c.responses as Array<{ option_index: number; count: number }>) ?? []
    const total = responses.reduce((s, r) => s + (r.count as number), 0)
    // Extract poll id from message if available (for voting)
    const pollId = (message as unknown as Record<string, unknown>).event_id as string | undefined
    return html`<div style="margin:6px 0;border:1px solid #e2e8f0;border-radius:8px;padding:10px">
      <div style="font-weight:600;margin-bottom:8px">${question}</div>
      <div style="display:flex;flex-direction:column;gap:6px">
        ${repeat(
          options,
          (opt) => opt.id,
          (opt, idx) => {
            const resp = responses.find((r) => r.option_index === idx)
            const count = resp?.count ?? 0
            const pct = total > 0 ? Math.round((count / total) * 100) : 0
            return html`<div style="border:1px solid #e2e8f0;border-radius:8px;padding:8px;background:#f8fafc">
              <div style="display:flex;justify-content:space-between;align-items:center;gap:8px">
                <span style="font-size:14px">${opt.text}</span>
                <span style="font-size:12px;color:#64748b">${count} votes · ${pct}%</span>
              </div>
              <div style="height:6px;background:#e2e8f0;border-radius:3px;margin-top:6px;overflow:hidden">
                <div style="width:${pct}%;height:100%;background:#4f46e5"></div>
              </div>
              <button
                style="margin-top:6px;font-size:12px;background:#4f46e5;color:white;border:none;border-radius:6px;padding:4px 10px;cursor:pointer"
                data-poll-id="${pollId ?? ""}"
                data-option-id="${opt.id}"
                data-option-index="${idx}"
                @click=${(e: Event) => {
                  const btn = e.currentTarget as HTMLElement
                  const pid = btn.dataset.pollId
                  const oid = btn.dataset.optionId
                  if (pid && oid) {
                    // Dispatch custom event for voting
                    btn.dispatchEvent(
                      new CustomEvent("poll-vote", {
                        bubbles: true,
                        composed: true,
                        detail: { pollId: pid, optionId: oid },
                      }),
                    )
                  }
                }}
              >Vote</button>
            </div>`
          },
        )}
      </div>
      ${total > 0 ? html`<div style="font-size:11px;color:#94a3b8;margin-top:6px">${total} total votes</div>` : ""}
    </div>`
  }
  // Fallback for any other type
  const body = (c.body as string | undefined) ?? ""
  if (body) return html`${body}`
  return html`<span style="color:#64748b">[${(c.type as string) ?? "unknown"}]</span>`
}

export function getContentPreview(message: Message, maxLen = 80): string {
  const c = message.content as unknown as Record<string, unknown>
  if (
    c.type === "redacted" ||
    (message as unknown as Record<string, unknown>).status === "redacted"
  )
    return "[Deleted]"
  if (c.type === "encrypted") return "[Encrypted]"
  if (c.type === "media")
    return `[${(c.kind as string) ?? "media"}] ${(c.filename as string) ?? ""}`.trim() || "[Media]"
  if (c.type === "location")
    return (c.description as string) || (c.geo_uri as string) || "[Location]"
  if (c.type === "poll") return (c.question as string) || "[Poll]"
  if (c.type === "unknown") return (c.fallback as string) || "[Unknown]"
  const body = (c.body as string | undefined) ?? ""
  if (!body) return "[Empty]"
  return body.length > maxLen ? `${body.slice(0, maxLen)}…` : body
}

export interface ReactionBarHandlers {
  onReactionClick: (e: Event) => void
  onReactionMouseEnter: (e: Event) => void
  onReactionMouseLeave: (e: Event) => void
  onReactionFocus: (e: Event) => void
  onReactionBlur: (e: Event) => void
  onReactionKeyDown: (e: KeyboardEvent) => void
  onReactionPointerDown: (e: PointerEvent) => void
  onReactionPointerMove: (e: PointerEvent) => void
  onReactionPointerUp: (e: PointerEvent) => void
  onReactionPointerCancel: (e: Event) => void
  onReactionPointerLeave: (e: Event) => void
  onReactionContextMenu: (e: Event) => void
}

export function renderPagination(
  page: number,
  totalPages: number,
  t: Messages,
  onPrev: (e: Event) => void,
  onNext: (e: Event) => void,
) {
  if (totalPages <= 1) return html``
  return html`<div class="pagination" part="pagination">
    <button ?disabled=${page <= 1} @click=${onPrev} aria-label="${t.prev}">${t.prev}</button>
    <span>${page} / ${totalPages}</span>
    <button ?disabled=${page >= totalPages} @click=${onNext} aria-label="${t.next}">${t.next}</button>
  </div>`
}

export interface CommentActions {
  onEdit: (e: Event) => void
  onDelete: (e: Event) => void
  onReply: (e: Event) => void
  onSave: (e: Event) => void
  onCancelEdit: (e: Event) => void
  onEditInput: (e: Event) => void
  onEditKeydown: (e: KeyboardEvent) => void
  onViewThread?: (e: Event) => void
}

export function renderReplyReference(target: Message | undefined, t: Messages) {
  if (!target) {
    return html`<div style="font-size:12px;color:#94a3b8;border-left:2px solid #e2e8f0;padding-left:8px;margin-bottom:6px;">${t.unavailableReference}</div>`
  }
  const preview = getContentPreview(target, 80)
  const name = normalizeDisplayName(target.author.display_name)
  // textContent safe, no HTML injection
  return html`<div style="font-size:12px;color:#64748b;border-left:2px solid #e2e8f0;padding-left:8px;margin-bottom:6px;">↩ ${name}: ${preview}</div>`
}

export function renderComment(
  vm: CommentViewModel,
  t: Messages,
  content: ReturnType<typeof renderContent>,
  reactions: unknown,
  opts: {
    isEditing: boolean
    editingDraft: string
    isDeleting?: boolean
    replyTarget?: Message | null
    actions: CommentActions
    /**
     * Hides the message-management (edit/delete/copy) affordances. Reply,
     * reactions, and poll voting remain available. Used by the Thread reader,
     * whose management policy differs from the main feed.
     */
    hideManagement?: boolean
    /** Visible label + accessible name for the View thread action (main feed only). */
    viewThreadLabel?: string
  },
) {
  const isRedacted =
    vm.message.content.type === "redacted" ||
    (vm.message as unknown as Record<string, unknown>).status === "redacted"
  return html`
    <div class="comment" part="comment" role="article">
      <div class="meta" part="meta">
        <cumments-avatar
          class="comment-avatar"
          .avatarUrl=${vm.avatarUrl}
          .displayName=${vm.displayName}
          .size=${32}
        ></cumments-avatar>
        <div class="meta-body">
          <div class="meta-identity">
            <span class="meta-name">${vm.displayName}</span> · ${new Date(vm.message.timestamp).toLocaleString()}
            ${vm.message.reply_to ? html` · <span>↩ ${t.reply}</span>` : ""}
          </div>
          <div class="meta-actions">
            <button
              style="font-size:11px;background:none;border:none;color:#4f46e5;cursor:pointer;padding:0 4px"
              data-event-id="${vm.message.event_id}"
              aria-label="${t.replyAriaLabel}"
              @click=${opts.actions.onReply}
            >${t.reply}</button>
            ${
              opts.actions.onViewThread
                ? html`<button
                  style="font-size:11px;background:none;border:none;color:#4f46e5;cursor:pointer;padding:0 4px"
                  data-event-id="${vm.message.event_id}"
                  aria-label="${opts.viewThreadLabel ?? t.viewThread}"
                  @click=${opts.actions.onViewThread}
                >${opts.viewThreadLabel ?? t.viewThread}</button>`
                : ""
            }
            <span style="position:relative;display:inline-block">
              ${
                opts.hideManagement
                  ? ""
                  : html`<button
                style="font-size:14px;background:none;border:none;color:#64748b;cursor:pointer;padding:0 8px"
                data-event-id="${vm.message.event_id}"
                aria-label="More actions"
                aria-haspopup="menu"
                aria-expanded="${(opts as unknown as { actionMenu?: unknown }).actionMenu ? "true" : "false"}"
                @click=${(opts.actions as unknown as { onMore?: (e: Event) => void }).onMore ?? (() => {})}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    ;(opts.actions as unknown as { onMore?: (e: Event) => void }).onMore?.(e)
                  }
                }}
              >⋯</button>`
              }
              ${(opts as unknown as { actionMenu?: unknown }).actionMenu ?? ""}
            </span>
          </div>
        </div>
      </div>
      ${vm.message.reply_to ? renderReplyReference(opts.replyTarget ?? undefined, t) : ""}
      ${
        opts.isEditing
          ? html`<div style="display:flex;gap:8px;margin:8px 0">
            <input
              part="input"
              aria-label="${t.editAriaLabel}"
              placeholder="${t.editPlaceholder}"
              .value=${opts.editingDraft}
              data-event-id="${vm.message.event_id}"
              @input=${opts.actions.onEditInput}
              @keydown=${opts.actions.onEditKeydown}
              style="flex:1;border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;font-size:14px"
            />
            <button
              style="background:#4f46e5;color:white;border:none;border-radius:8px;padding:6px 12px;cursor:pointer"
              data-event-id="${vm.message.event_id}"
              aria-label="${t.save}"
              @click=${opts.actions.onSave}
            >${t.save}</button>
            <button
              style="background:white;border:1px solid #e2e8f0;border-radius:8px;padding:6px 12px;cursor:pointer"
              data-event-id="${vm.message.event_id}"
              aria-label="${t.cancel}"
              @click=${opts.actions.onCancelEdit}
            >${t.cancel}</button>
          </div>`
          : html`<div part="body">${content}</div>`
      }
      ${!opts.isEditing && !isRedacted ? html`${reactions}` : ""}
    </div>
  `
}

export function renderIdentityCapsule(
  profile: import("../api/visitors").VisitorProfile | null,
  _t: Messages,
  open: boolean,
  onToggle: (e: Event) => void,
) {
  const name = normalizeDisplayName(profile?.display_name)
  return html`<button
    part="identity-capsule"
    aria-label="Identity"
    aria-haspopup="dialog"
    aria-expanded="${open ? "true" : "false"}"
    @click=${onToggle}
    style="display:flex;align-items:center;gap:8px;border:1px solid #e2e8f0;border-radius:999px;padding:4px 10px;background:white;cursor:pointer;max-width:160px"
  >
    <cumments-avatar
      .avatarUrl=${profile?.avatar_url ?? null}
      .displayName=${name}
      .size=${24}
    ></cumments-avatar>
    <span style="font-size:13px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:80px" class="capsule-name">${name}</span>
  </button>`
}

/** A switchable identity row: locally known information only, never fetched. */
export interface IdentityRow {
  publicKey: string
  /** Known display name, or "" when no local profile information exists. */
  displayName: string
  avatarUrl: string | null
  active: boolean
}

const shortKey = (publicKey: string, length = 8): string => publicKey.slice(0, length)

const identitySection = "margin-bottom:12px"

const sectionHeading =
  "font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:#94a3b8;margin-bottom:6px"

function renderIdentityRow(row: IdentityRow, onSwitch: (e: Event) => void) {
  const knownName = row.displayName.trim()
  // Prefer a useful display name; otherwise fall back to the public-key prefix,
  // which is the only stable local identity information available.
  const primary = knownName || `${shortKey(row.publicKey, 12)}…`
  return html`<div
    data-identity-row="${row.publicKey}"
    data-active="${row.active ? "true" : "false"}"
    style="display:flex;align-items:center;gap:8px;padding:8px;border:1px solid ${row.active ? "#4f46e5" : "#e2e8f0"};border-radius:8px;background:${row.active ? "#eef2ff" : "white"}"
  >
    <cumments-avatar
      .avatarUrl=${row.avatarUrl}
      .displayName=${knownName || row.publicKey}
      .size=${28}
    ></cumments-avatar>
    <div style="flex:1;min-width:0">
      <div data-identity-name style="font-size:12px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${primary}</div>
      ${
        knownName
          ? html`<div style="font-size:11px;color:#64748b;font-family:monospace;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${shortKey(row.publicKey, 16)}…</div>`
          : ""
      }
    </div>
    ${
      row.active
        ? html`<span
            data-identity-active="true"
            aria-label="Active identity"
            style="color:#4f46e5;font-size:12px;flex-shrink:0"
          >✓</span>`
        : html`<button
            data-public-key="${row.publicKey}"
            @click=${onSwitch}
            style="background:white;border:1px solid #e2e8f0;border-radius:4px;padding:4px 8px;cursor:pointer;flex-shrink:0;font-size:11px"
          >Switch</button>`
    }
  </div>`
}

/**
 * Identity card, split into three visually distinct concerns:
 * the active profile (how this identity appears), switching between local
 * identities, and lower-priority identity management actions.
 *
 * Rows carry only locally known information — the caller resolves display
 * names from cached profiles, so opening the card never fetches per identity.
 */
export function renderIdentityPopover(
  _t: Messages,
  opts: {
    profile: import("../api/visitors").VisitorProfile | null
    activePublicKey: string | null
    rows: IdentityRow[]
  },
  onSwitch: (e: Event) => void,
  onCreate: (e: Event) => void,
  onImport: (e: Event) => void,
  onManage: (e: Event) => void,
  onClose: (e: Event) => void,
  onProfile: (e: Event) => void,
) {
  const profileName = normalizeDisplayName(opts.profile?.display_name)
  const activeKey = opts.activePublicKey
  return html`<div
    role="dialog"
    aria-label="Identity"
    style="position:absolute;top:100%;right:0;margin-top:8px;min-width:280px;max-width:320px;background:white;border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 4px 12px rgba(0,0,0,0.1);padding:12px;z-index:10"
  >
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
      <span style="font-weight:600">Identity</span>
      <button @click=${onClose} aria-label="Close" style="background:none;border:none;cursor:pointer">×</button>
    </div>

    <section data-identity-section="profile" style="${identitySection}">
      <div style="${sectionHeading}">Current profile</div>
      <div style="display:flex;align-items:center;gap:10px;padding:10px;border:1px solid #e2e8f0;border-radius:8px;background:#f8fafc">
        <cumments-avatar
          .avatarUrl=${opts.profile?.avatar_url ?? null}
          .displayName=${profileName}
          .size=${36}
        ></cumments-avatar>
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${profileName}</div>
          <div style="font-size:11px;color:#64748b;font-family:monospace;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${activeKey ? `${shortKey(activeKey, 16)}…` : "No active identity"}</div>
          <div style="font-size:11px;color:#94a3b8">How you appear publicly</div>
        </div>
      </div>
      <button
        @click=${onProfile}
        aria-label="Edit profile"
        style="width:100%;text-align:left;background:white;border:1px solid #e2e8f0;border-radius:8px;padding:8px 12px;cursor:pointer;font-size:13px;margin-top:6px"
      >Profile — display name and avatar</button>
    </section>

    <section data-identity-section="switch" style="${identitySection}">
      <div style="${sectionHeading}">Switch identity</div>
      <div style="display:flex;flex-direction:column;gap:8px;max-height:200px;overflow-y:auto">
        ${repeat(
          opts.rows,
          (row) => row.publicKey,
          (row) => renderIdentityRow(row, onSwitch),
        )}
      </div>
    </section>

    <section data-identity-section="manage">
      <div style="${sectionHeading}">Identity management</div>
      <div style="display:flex;gap:8px">
        <button @click=${onCreate} style="flex:1;background:white;border:1px solid #e2e8f0;border-radius:6px;padding:8px;cursor:pointer;font-size:12px">Create</button>
        <button @click=${onImport} style="flex:1;background:white;border:1px solid #e2e8f0;border-radius:6px;padding:8px;cursor:pointer;font-size:12px">Import</button>
        <button @click=${onManage} style="flex:1;background:white;border:1px solid #e2e8f0;border-radius:6px;padding:8px;cursor:pointer;font-size:12px">Manage</button>
      </div>
    </section>
  </div>`
}

export function renderActionMenu(
  _t: Messages,
  isOwn: boolean,
  onEdit: (e: Event) => void,
  onCopyLink: (e: Event) => void,
  onDelete: (e: Event) => void,
  _onClose: (e: Event) => void,
  eventId?: string,
  onKeyDown?: (e: KeyboardEvent) => void,
) {
  return html`<div role="menu" @keydown=${onKeyDown ?? (() => {})} style="position:absolute;top:100%;right:0;margin-top:4px;min-width:160px;background:white;border:1px solid #e2e8f0;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.1);padding:4px;z-index:10">${isOwn ? html`<button role="menuitem" aria-label="Edit comment" data-event-id="${eventId ?? ""}" @click=${onEdit} style="width:100%;text-align:left;background:none;border:none;padding:8px 12px;cursor:pointer">Edit</button>` : ""}<button role="menuitem" aria-label="Copy link" data-event-id="${eventId ?? ""}" @click=${onCopyLink} style="width:100%;text-align:left;background:none;border:none;padding:8px 12px;cursor:pointer">Copy link</button>${isOwn ? html`<button role="menuitem" aria-label="Delete comment" data-event-id="${eventId ?? ""}" @click=${onDelete} style="width:100%;text-align:left;background:none;border:none;padding:8px 12px;cursor:pointer;color:#ef4444">Delete</button>` : ""}</div>`
}

export function renderDeleteDialog(
  _t: Messages,
  onCancel: (e: Event) => void,
  onConfirm: (e: Event) => void,
  eventId?: string,
  onKeyDown?: (e: KeyboardEvent) => void,
) {
  return html`<div role="dialog" aria-modal="true" aria-labelledby="delete-title" @keydown=${onKeyDown ?? (() => {})} style="position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:100;padding:16px" @click=${(
    e: Event,
  ) => {
    if (e.target === e.currentTarget) onCancel(e)
  }}>
    <div style="background:white;border-radius:12px;padding:20px;max-width:400px;width:100%;box-shadow:0 8px 24px rgba(0,0,0,0.2)">
      <h3 id="delete-title" style="margin:0 0 8px;font-size:16px;font-weight:600">Delete comment?</h3>
      <p style="margin:0 0 16px;font-size:14px;color:#64748b">Cannot be undone.</p>
      <div style="display:flex;gap:12px;justify-content:flex-end">
        <button @click=${onCancel} style="background:white;border:1px solid #e2e8f0;border-radius:8px;padding:8px 16px;cursor:pointer">Cancel</button>
        <button data-event-id="${eventId ?? ""}" @click=${onConfirm} style="background:#ef4444;color:white;border:none;border-radius:8px;padding:8px 16px;cursor:pointer">Delete</button>
      </div>
    </div>
  </div>`
}

export function renderReactionPicker(_t: Messages, onSelect: (e: Event) => void) {
  const emojis = ["❤️", "👍", "😂", "🎉", "😮", "😢", "👏", "🔥"]
  return html`<div role="dialog" aria-label="Pick reaction" class="quick-reaction-palette" style="position:fixed;top:0;left:0;background:white;border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 4px 12px rgba(0,0,0,0.1);padding:6px;display:flex;gap:4px;flex-wrap:wrap;max-width:calc(100vw - 16px);z-index:1000">
    ${repeat(
      emojis,
      (e) => e,
      (emoji) =>
        html`<button @click=${onSelect} data-reaction-key="${emoji}" style="width:32px;height:32px;border:1px solid #e2e8f0;border-radius:8px;background:white;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;padding:0">${emoji}</button>`,
    )}
  </div>`
}

export function renderIdentityDialog(
  title: string,
  content: unknown,
  _t: Messages,
  onClose: (e: Event) => void,
  onKeyDown?: (e: KeyboardEvent) => void,
) {
  return html`<div role="dialog" aria-modal="true" aria-labelledby="identity-dialog-title" style="position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:100;padding:16px" @click=${(
    e: Event,
  ) => {
    if (e.target === e.currentTarget) onClose(e)
  }} @keydown=${onKeyDown ?? (() => {})}>
    <div style="background:white;border-radius:12px;padding:20px;max-width:480px;width:100%;max-height:80vh;overflow-y:auto;box-shadow:0 8px 24px rgba(0,0,0,0.2)">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <h3 id="identity-dialog-title" style="margin:0;font-size:16px;font-weight:600">${title}</h3>
        <button @click=${onClose} aria-label="Close" style="background:none;border:none;cursor:pointer;font-size:20px;color:#64748b">×</button>
      </div>
      <div>${content}</div>
    </div>
  </div>`
}

/**
 * Thread reader dialog shell (class-based styles live in cumments-comments).
 * Root/members are rendered by the caller with the canonical comment renderer.
 * All status branching is presentation-only: membership/pagination decisions
 * come from ThreadFeature's backend-derived state.
 */
export function renderThreadDialog(
  t: Messages,
  opts: {
    loading: boolean
    error: string | null
    hasMembers: boolean
    hasNextPage: boolean
    /** Backend-derived active reply count (meta.total); null while unknown. */
    total: number | null
  },
  rootContent: unknown,
  membersContent: unknown,
  composerSlot: unknown,
  onClose: (e: Event) => void,
  onRetry: (e: Event) => void,
  onLoadMore: (e: Event) => void,
  onKeyDown?: (e: KeyboardEvent) => void,
) {
  return html`<div
    class="thread-dialog"
    part="thread-dialog"
    role="dialog"
    aria-modal="true"
    aria-label="${t.thread}"
    @keydown=${onKeyDown ?? (() => {})}
    @click=${(e: Event) => {
      if (e.target === e.currentTarget) onClose(e)
    }}
  >
    <div class="thread-panel" part="thread-panel">
      <div class="thread-header" part="thread-header">
        <h3>${t.thread}${opts.total != null ? html` · ${opts.total}` : ""}</h3>
        <button class="thread-close" part="thread-close" aria-label="${t.close}" @click=${onClose}>×</button>
      </div>
      <div class="thread-body" part="thread-body">
        <div class="thread-root" part="thread-root">${rootContent}</div>
        <div class="thread-section">${t.threadReplies}</div>
        ${
          opts.error
            ? html`<div class="error" role="alert" aria-live="assertive">${opts.error}</div>
        <div class="thread-actions">
          <button style="background:var(--cumments-primary,#4f46e5);color:white;border:none" aria-label="${t.retry}" @click=${onRetry}>${t.retry}</button>
          <button style="background:white;border:1px solid var(--cumments-border,#e2e8f0)" aria-label="${t.close}" @click=${onClose}>${t.close}</button>
        </div>`
            : html`${opts.loading ? html`<div class="thread-status" role="status" aria-live="polite">${t.loading}</div>` : ""}
        ${!opts.loading && !opts.hasMembers ? html`<div class="empty">${t.noReplies}</div>` : ""}
        ${membersContent}
        ${
          opts.hasNextPage
            ? html`<div class="thread-load-more">
              <button ?disabled=${opts.loading} aria-label="${t.loadMore}" @click=${onLoadMore}>${t.loadMore}</button>
            </div>`
            : ""
        }`
        }
      </div>
      <div class="thread-composer" part="thread-composer">${composerSlot}</div>
    </div>
  </div>`
}
